/** Expenses hooks with mock fallback (same contract as prior phases). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EXPENSES_QUERY_PREFIX, REPORTS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Expense } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { expensesApi } from "./expenses.api";
import { toExpense, toExpenseWriteBody, toMockExpense, type ExpenseInput } from "./expenses.adapter";
import { useExpensesStore } from "./expenses.store";

const LIST_PARAMS = { limit: 100, sortBy: "date", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseExpensesResult {
  expenses: Expense[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useExpenses(): UseExpensesResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useExpensesStore((s) => s.active);

  const query = useQuery({
    queryKey: queryKeys.expensesList(LIST_PARAMS),
    queryFn: () => expensesApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { expenses: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { expenses: query.data.data.map(toExpense), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { expenses: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  return { expenses: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

function useInvalidateExpenses() {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: EXPENSES_QUERY_PREFIX }),
    qc.invalidateQueries({ queryKey: REPORTS_QUERY_PREFIX }),
  ]);
}

export function useCreateExpense() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const add = useExpensesStore((s) => s.add);
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: async (input: ExpenseInput) => {
      if (authed) return expensesApi.create(toExpenseWriteBody(input));
      const mock = toMockExpense(input);
      add(mock);
      return mock;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useUpdateExpense() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = useExpensesStore((s) => s.update);
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ExpenseInput }) => {
      if (authed) return expensesApi.update(id, toExpenseWriteBody(input));
      update(id, { ...input });
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useArchiveExpense() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const archive = useExpensesStore((s) => s.archive);
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return expensesApi.archive(id);
      archive(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useRestoreExpense() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const restore = useExpensesStore((s) => s.restore);
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return expensesApi.restore(id);
      restore(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

const ARCHIVED_PARAMS = { archived: true, limit: 100, sortBy: "date", sortOrder: "desc" as const };

export function useArchivedExpenses() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const query = useQuery({
    queryKey: queryKeys.expensesList(ARCHIVED_PARAMS),
    queryFn: () => expensesApi.list(ARCHIVED_PARAMS),
    enabled: authed,
  });
  if (query.data) return { expenses: query.data.data.map(toExpense), isLoading: false, isError: false, refetch: query.refetch };
  if (query.isError) return { expenses: [] as Expense[], isLoading: false, isError: true, refetch: query.refetch };
  return { expenses: [] as Expense[], isLoading: query.isLoading, isError: false, refetch: query.refetch };
}
